import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Caption, LabelM } from '@sorare/core/src/atoms/typography';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import TokenStatsIcon from 'components/token/TokenStatsIcon';

import { FootballRivalsDraftToken_card } from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ center: true, gap: 0.5 })`
  display: grid;
  grid-template-columns: 1fr var(--quadruple-unit) 1fr;
  > * {
    flex-shrink: 0;
  }
  @media ${tabletAndAbove} {
    gap: var(--unit);
  }
`;

const BonusRoot = styled(Horizontal)`
  background-color: var(--c-nd-100);
  color: rgb(var(--c-rgb-limited));
  padding: var(--half-unit);
  border-radius: var(--half-unit);
  display: flex;
  justify-content: center;
  width: calc(6 * var(--unit));
`;

const BoldLabelM = styled(LabelM)`
  font-weight: bold;
`;
const TokenStatsIconCtn = styled(Horizontal)`
  justify-content: flex-end;
`;

const Bonus = ({ bonus }: { bonus: number }) => {
  return (
    <BonusRoot>
      <BoldLabelM as="span">
        +{(bonus * 100 - 100).toFixed(0)}
        <Caption as="span">%</Caption>
      </BoldLabelM>
    </BonusRoot>
  );
};

type Props = {
  card: FootballRivalsDraftToken_card;
  onInfoClick?: (cardSlug: string, playerSlug: string) => void;
};

export const FootballRivalsDraftToken = ({ card, onInfoClick }: Props) => {
  const { averageScore, slug, anyPlayer, rivalsPower } = card;

  return (
    <Root>
      {onInfoClick && (
        <TokenStatsIconCtn>
          <TokenStatsIcon
            card={card}
            onInfoClick={() => onInfoClick(slug, anyPlayer.slug)}
          />
        </TokenStatsIconCtn>
      )}
      <Cost score={averageScore!} />
      <Bonus bonus={Number(rivalsPower)} />
    </Root>
  );
};

FootballRivalsDraftToken.fragments = {
  card: gql`
    fragment FootballRivalsDraftToken_card on Card {
      slug
      anyPlayer {
        slug
      }
      averageScore(type: LAST_FIFTEEN_SO5_AVERAGE_SCORE)
      rarityTyped
      rivalsPower
      ...TokenStatsIcon_anyCard
    }
    ${TokenStatsIcon.fragments.anyCard}
  ` as TypedDocumentNode<FootballRivalsDraftToken_card>,
};

export default FootballRivalsDraftToken;
